import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { StoreContext } from '@store/Root.store'

import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { cx } from '@/utils/cx'
import {
  Button,
  CountrySelect,
  DocumentCheckbox,
  Header,
  Icon,
  IconTypes,
  LoadingSpinner,
  SignaturePad,
  SignaturePadHandle,
} from '@/components/Generic'
import { EIDV_Country, mapCountriesArray } from '@/methods/mapCountries'
import { SelectOption } from '@/components/Generic/SelectInput/SelectInput'
import { CheckedEvent } from '@/types/generic'
import videoid_terms from '../assets/videoid_terms.pdf'
import { Illustration } from '@/components/Illustration/Illustration'
import { defaultIllustrations } from '@/components/Illustration/constants'
import { DownloadDocumentButton } from '@/components/DownloadDocumentButton/DownloadDocumentButton'
import { getPermissionCheckerErrorIcon } from '@/components/Illustration/helpers'

export const ThemePage = observer(() => {
  const store = useContext(StoreContext)
  const { isMobile, userAgent } = store.AppState
  const { themeId } = store.InterfaceState
  const { browser, system, isMobile: isMobileOS } = userAgent

  const [terms, setTerms] = useState(false)
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const signaturePadRef = useRef<SignaturePadHandle>(null)
  const [isSignatureEmpty, setIsSignatureEmpty] = useState(true)
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null)
  const [country, setCountry] = useState<EIDV_Country | null>(null)
  const [apiCountries] = useState<EIDV_Country[]>(
    mapCountriesArray({
      FR: {
        countryName: 'France',
        countryNameEN: 'France',
        countryIcon: '🇫🇷',
      },

      DE: {
        countryName: 'Deutschland',
        countryNameEN: 'Germany',
        countryIcon: '🇩🇪',
      },

      IT: {
        countryName: 'Italia',
        countryNameEN: 'Italy',
        countryIcon: '🇮🇹',
      },

      NO: {
        countryName: 'Norge',
        countryNameEN: 'Norway',
        countryIcon: '🇳🇴',
      },
    })
  )

  const handleCountry = (country: SelectOption | null) => {
    if (country) {
      const countryApiObj =
        apiCountries.find((apiObj) => apiObj.countryCode === country.value) ?? null

      setCountry(countryApiObj)
    } else {
      setCountry(null)
    }
  }

  const handleTerms = ({ checked: value }: CheckedEvent) => {
    setTerms(value)
  }

  const accordionOpenHandler = () => {
    setIsAccordionOpen(!isAccordionOpen)
  }

  return (
    <div className="page-container mobile-m-0">
      <Header>Theme {themeId}</Header>

      <h2>Loading spinner</h2>
      <LoadingSpinner />

      <h2>Paragraph</h2>
      <p className="generic__paragraph">
        Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has
        been the industry's standard dummy text ever since the 1500s, when an unknown printer took a
        galley of type and scrambled it to make a type specimen book.
      </p>

      <div
        className={cx('generic__paragraph-centered', 'flex-row-center', 'generic__error-message')}
      >
        <Icon type="info" size="18px" color="var(--icon-error-color, #941616)" />
        Incorrect pin code, please try again.
      </div>

      <h2>Illustration</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        {(Object.keys(defaultIllustrations) as Array<keyof typeof defaultIllustrations>).map(
          (key) => (
            <div
              key={key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                width: 'calc(50% - 12px)',
              }}
            >
              <>{key}</>
              <Illustration type={key} />
            </div>
          )
        )}
      </div>

      <h2>Icons</h2>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {IconTypes.map((iconType) => (
          <div
            key={iconType}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
          >
            <Icon type={iconType} />
            <span style={{ fontSize: '12px' }}>{iconType}</span>
          </div>
        ))}
      </div>

      <h2>
        Permission checker icon - system: {system}, browser: {browser}, isMobileOS:{' '}
        {isMobileOS ? 'true' : 'false'}
      </h2>
      <Illustration
        type={getPermissionCheckerErrorIcon(system ?? '', browser ?? '', isMobileOS ?? false)}
      />

      <h2>DocumentCheckbox</h2>
      <DocumentCheckbox
        title="Terms and Conditions of use"
        isRequired
        showAsterisk
        inputName="terms-checkbox"
        description="I accept the terms and conditions of the video-identification process"
        externalUrl={videoid_terms}
        handleDecision={handleTerms}
        value={terms}
        goToDocumentLabel="Go to document"
      />

      <h2>Select</h2>
      <div className="generic__country-wrap">
        <span>Select input</span>
        <CountrySelect
          externalList={apiCountries}
          value={country}
          onChange={handleCountry}
          autoFocus={!!isMobile}
          defaultMenuIsOpen={false}
          placeholder="Select country"
          showCheckmark
        />
      </div>

      <div className="elavon__paragraph-with-icon">
        <p className="generic__paragraph">Firstname</p>
        {false ? (
          <Icon type="mismatch" size="26px" color="#D83535" />
        ) : (
          <Icon type="check-circle" size="26px" color="#467723" />
        )}
      </div>

      <div className="elavon__expected-name-wrapper">Max Mustermann</div>
      <div className={cx('elavon__actual-name-wrapper', 'error')}>Erika Mustermann</div>

      <h2>Document type picker</h2>
      <div className="generic__document-type-wrap">
        <h2 className="generic__document-type-label">Choose a document type</h2>
        <div
          className={cx('generic__document-picker', 'generic__current')}
          onClick={() => console.log('Identity card selected')}
          tabIndex={0}
          role="button"
          onKeyDown={(event) =>
            onKeyDownHandler(event, () => console.log('Identity card selected'))
          }
        >
          <Icon type="identity-card" size="32px" />
          <div className="generic__document-picker-content">Identity card</div>
          <div className="generic__document-checked">
            <Icon type="selected-document" size="32px" color="var(--symbol-current, black)" />
          </div>
        </div>

        <div
          className="generic__document-picker"
          onClick={() => console.log('Passport selected')}
          tabIndex={0}
          role="button"
          onKeyDown={(event) => onKeyDownHandler(event, () => console.log('Passport selected'))}
        >
          <Icon type="passport" size="32px" />
          <div className="generic__document-picker-content">Passport</div>
        </div>
      </div>

      <h2>Accordion</h2>
      <div className={cx('switch-to-mobile__accordion-wrapper')}>
        <div
          className={cx(
            'switch-to-mobile__accordion-title',
            'open-accordion',
            isAccordionOpen && 'switch-to-mobile__accordion-title--open'
          )}
          tabIndex={0}
          role="button"
          aria-expanded={isAccordionOpen}
          aria-controls="accordion-other-options"
          onClick={accordionOpenHandler}
          onKeyDown={(event) => onKeyDownHandler(event, () => accordionOpenHandler())}
        >
          <h2>Open accordion</h2>
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="7"
            viewBox="0 0 10 7"
            fill="none"
          >
            <path
              d="M1 1.28369L5 5.28369L9 1.28369"
              stroke="#34353A"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <div
          id="accordion-other-options"
          className={cx(
            'switch-to-mobile__accordion',
            isAccordionOpen && 'switch-to-mobile__accordion--open'
          )}
          aria-hidden={!isAccordionOpen}
        >
          <p className="generic__intro-paragraph">
            It has survived not only five centuries, but also the leap into electronic typesetting,
            remaining essentially unchanged. It was popularised in the 1960s with the release of
            Letraset sheets containing Lorem Ipsum passages, and more recently with desktop
            publishing software like Aldus PageMaker including versions of Lorem Ipsum.
          </p>
        </div>
      </div>

      <h2>Signature pad</h2>
      <div className="generic__container">
        <SignaturePad ref={signaturePadRef} onChange={setIsSignatureEmpty} />

        <div className="layout__buttons-container">
          <Button
            onClick={() => {
              signaturePadRef.current?.clear()
              setSignaturePreview(null)
            }}
            className="button--link"
            disabled={isSignatureEmpty}
          >
            <span className="generic__button-text">Clear</span>
          </Button>
          <Button
            onClick={() => setSignaturePreview(signaturePadRef.current?.toDataURL() ?? null)}
            className="button--primary"
            disabled={isSignatureEmpty}
          >
            <span className="generic__button-text">Capture</span>
          </Button>
        </div>

        {signaturePreview && (
          <img
            src={signaturePreview}
            alt="Captured signature preview"
            style={{
              marginTop: '12px',
              maxWidth: '100%',
              border: '1px solid #c4c4c4',
              borderRadius: '8px',
            }}
          />
        )}
      </div>

      <h2>Button primary</h2>
      <div className="generic__container">
        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--primary">
            <span className="generic__button-text">Button primary</span>
          </Button>
        </div>

        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--primary" disabled>
            <span className="generic__button-text">Button primary disabled</span>
          </Button>
        </div>

        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--primary">
            <LoadingSpinner inButton />
            <span className="generic__button-text generic__button-text--loading">Proceed</span>
          </Button>
        </div>
      </div>

      <h2>Button secondary</h2>
      <div className="generic__container">
        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--secondary">
            <span className="generic__button-text">Button secondary</span>
          </Button>
        </div>

        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--secondary" disabled>
            <span className="generic__button-text">Button secondary disabled</span>
          </Button>
        </div>

        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--secondary">
            <LoadingSpinner inButton />
            <span className="generic__button-text generic__button-text--loading">Proceed</span>
          </Button>
        </div>
      </div>

      <h2>Button link</h2>
      <div className="generic__container">
        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--link">
            <span className="generic__button-text">Button link</span>
          </Button>
        </div>

        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--link" disabled>
            <span className="generic__button-text">Button link disabled</span>
          </Button>
        </div>

        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--link">
            <LoadingSpinner inButton />
            <span className="generic__button-text generic__button-text--loading">Proceed</span>
          </Button>
        </div>
      </div>

      <h2>Two buttons</h2>
      <div className="generic__container">
        <div className="layout__buttons-container">
          <DownloadDocumentButton
            documentUrl={videoid_terms}
            margin="0"
            onClick={() => console.log('Download document')}
          />
          <Button onClick={() => {}} className="button--primary">
            <LoadingSpinner inButton />
            <span className="generic__button-text generic__button-text--loading">Proceed</span>
          </Button>
        </div>
      </div>

      <h2>Two buttons</h2>
      <div className="generic__container">
        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--primary">
            <span className="generic__button-text">Back</span>
          </Button>
          <Button onClick={() => {}} className="button--primary">
            <LoadingSpinner inButton />
            <span className="generic__button-text generic__button-text--loading">Proceed</span>
          </Button>
        </div>
      </div>

      <h2>Two buttons</h2>
      <div className="generic__container">
        <div className="layout__buttons-container">
          <Button onClick={() => {}} className="button--secondary">
            <span className="generic__button-text">Back</span>
          </Button>
          <Button onClick={() => {}} className="button--primary">
            <span className="generic__button-text">Proceed</span>
          </Button>
        </div>
      </div>

      <div className="layout__content-container-bottom-fixed">
        {!isMobile && <div className="layout__content-container-bottom-left" />}
        <Button onClick={() => {}} className="button--primary">
          <span className="generic__button-text">Continue</span>
        </Button>
      </div>
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </div>
  )
})
